// WHAT: Recent Transactions Hook
// WHY:  Fetches recent transactions from Supabase with configurable limit
// DOMAIN: Investments → Parqet
// DEPENDENCIES: Supabase client service
// RISKS:
//   - NOTE: Orders by both transaction_date and created_at (may cause unexpected ordering)
//   - NOTE: Error handling logs to console (may not be visible to user)
//   - NOTE: Hook refetches when limit changes (may cause unnecessary requests)

import { useEffect, useState, useCallback } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";
import { getSupabaseClient } from "@/shared/services";

export interface RecentTransaction {
  [key: string]: unknown;
  account_id: null | string;
  amount: number;
  broker: null | string;
  created_at: string;
  currency: string;
  id: string;
  pdf_filename: null | string;
  security_id: string;
  trace_id: null | string;
  transaction_date: string;
  transaction_hash: string;
  type: string;
}

/**
 * WHAT:
 *   Custom hook for fetching recent transactions from Supabase.
 *
 * WHY:
 *   Provides transaction history with configurable limit and loading states.
 *
 * INPUTS:
 *   - limit: number — Maximum number of transactions to fetch (default: 20)
 *
 * OUTPUT:
 *   - transactions: RecentTransaction[] — Array of recent transactions
 *   - loading: boolean — Loading state
 *   - error: string | null — Error message if fetch failed
 *   - refetch: () => Promise<void> — Function to manually refetch transactions
 *
 * BEHAVIOR:
 *   - Fetches transactions on mount and when limit changes
 *   - Orders by transaction_date descending, then created_at descending
 *   - Limits results to specified count
 *   - Sets loading state during fetch
 *   - Returns empty array and error message on failure
 *   - Provides refetch function for manual refresh
 *
 * RISKS:
 *   - NOTE: Dual ordering (transaction_date, created_at) may cause unexpected results
 *   - NOTE: Error logging uses console.error (may not be visible in production)
 *   - NOTE: Hook refetches when limit changes (may cause unnecessary requests)
 *
 * @param limit - Maximum number of transactions to fetch
 *
 * @returns {object} Object containing transactions, loading, error, and refetch function
 *
 */
export const useRecentTransactions = (limit = 20) => {
  const [transactions, setTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);

      const supabase = await getSupabaseClient();
      const { data, error: fetchError } = await supabase
        .from("transactions_all")
        .select("*")
        .order("transaction_date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(limit);

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      setTransactions(data ?? []);
    } catch (error_) {
      const errorMessage =
        error_ instanceof Error ? error_.message : "Failed to fetch recent transactions";
      setError(errorMessage);
      debugLog("error", "investments", "Error fetching recent transactions", {
        error: errorMessage,
        file: "useRecentTransactions.ts",
        function: "fetchTransactions",
        module: "parqet",
      });
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    void fetchTransactions();
  }, [fetchTransactions]);

  return {
    error,
    loading,
    refetch: fetchTransactions,
    transactions,
  };
};
